import { Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { CustomBenchmarkSuccessResponse } from '@/api/rest/resources/types/benchmark';
import { PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import { ChartHeading } from '@/components/Charts/components/ChartHeading';
import { useChartZoom } from '@/components/Charts/components/ChartZoom';
import { NDVIHistoricalChart } from '@/components/Charts/NDVIHistorical';
import { GraphFact } from '@/components/Charts/types';

import { NoPlotData } from '../NoPlotData';

export const NDVIGraphTile = ({
  data,
  benchmarks,
  highBenchmark,
  moderateBenchmark,
  lowBenchmark,
  activeCustomBenchmark,
  selectedBenchmarkId,
  onBenchmarkSelect,
}: {
  data: GraphFact[] | null | undefined;
  benchmarks: number[];
  highBenchmark: PlotReportFactElement<GraphFact[] | null> | undefined;
  moderateBenchmark: PlotReportFactElement<GraphFact[] | null> | undefined;
  lowBenchmark: PlotReportFactElement<GraphFact[] | null> | undefined;
  activeCustomBenchmark?: CustomBenchmarkSuccessResponse | null;
  selectedBenchmarkId: string | null;
  onBenchmarkSelect: (id: string | null) => void;
}) => {
  const { t } = useTranslation();
  const { zoom, setZoom } = useChartZoom();

  const lowBenchmarkData = (lowBenchmark?.value ?? [])?.map((fact) => {
    return { ...fact, name: `${fact.name}_low` };
  });
  const moderateBenchmarkData = (moderateBenchmark?.value ?? [])?.map((fact) => {
    return { ...fact, name: `${fact.name}_moderate` };
  });
  const highBenchmarkData = (highBenchmark?.value ?? [])?.map((fact) => {
    return { ...fact, name: `${fact.name}_high` };
  });

  const noChartData = !data || data.length === 0;

  if (noChartData) {
    return <NoPlotData />;
  }

  const chartData = [...data, ...lowBenchmarkData, ...moderateBenchmarkData, ...highBenchmarkData];

  return (
    <Stack center data-testid='ndvi-graph-tile'>
      <Stack spacing={2.5} className='w-full items-center justify-center px-1 py-6 lg:items-start lg:p-8 lg:pl-1'>
        <ChartHeading
          heading={t('shared.ncaDetail.details.ndvi.labels.graphHeadline')}
          className='w-full px-6 sm:px-0'
          popover={{
            title: t('shared.ncaDetail.details.ndvi.labels.graphHeadline'),
            body: t('shared.ncaDetail.details.ndvi.labels.graphHeadlineTooltip'),
          }}
          zoom={zoom}
          setZoom={setZoom}
          indicator={GraphTileType.ndvi}
          selectedBenchmark={selectedBenchmarkId}
          setSelectedBenchmark={onBenchmarkSelect}
        />
        <NDVIHistoricalChart
          data={chartData}
          benchmarks={benchmarks}
          customBenchmark={activeCustomBenchmark}
          zoom={zoom}
          setZoom={setZoom}
        />
      </Stack>
    </Stack>
  );
};
